import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowUpDown,
  CalendarDays,
  LayoutGrid,
  List,
  Mail,
  MapPin,
  MoreHorizontal,
  Pencil,
  Phone,
  Plus,
  Search,
  Store,
  Tag,
} from "lucide-react";
import client, { unwrap, apiErrorMessage } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { showroomImageFor } from "@/lib/stockImages";
import ShowroomDialog from "@/components/showrooms/ShowroomDialog";

const ALL = "all";

const SORTS = {
  latest: { label: "Latest added", fn: (a, b) => new Date(b.createdAt) - new Date(a.createdAt) },
  oldest: { label: "Oldest first", fn: (a, b) => new Date(a.createdAt) - new Date(b.createdAt) },
  name: { label: "Name (A–Z)", fn: (a, b) => a.name.localeCompare(b.name) },
};

const formatDate = (d) => new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
const coverFor = (s) => s.images?.find((img) => img.url)?.url || showroomImageFor(s._id);
const fullAddress = (s) => [s.address, s.city].filter(Boolean).join(", ");

function StatusChip({ active }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-card/95 shadow-sm px-2.5 py-1 text-xs font-medium text-foreground">
      <span className={cn("h-1.5 w-1.5 rounded-full", active ? "bg-success" : "bg-muted-foreground/60")} />
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function useToggleActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (s) => client.patch(`/showrooms/${s._id}`, { isActive: !s.isActive }),
    onSuccess: (_, s) => {
      toast.success(`${s.name} marked ${s.isActive ? "inactive" : "active"}`);
      queryClient.invalidateQueries({ queryKey: ["showrooms"] });
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Couldn't update the showroom")),
  });
}

function ShowroomMenu({ s, onEdit, className }) {
  const toggle = useToggleActive();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button aria-label={`More actions for ${s.name}`} data-testid={`showroom-menu-${s._id}`} className={className}>
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => onEdit(s)}>Edit details</DropdownMenuItem>
        <DropdownMenuItem onClick={() => toggle.mutate(s)}>{s.isActive ? "Mark inactive" : "Mark active"}</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ContactLine({ icon: Icon, children, href, missing }) {
  return (
    <p className="flex items-center gap-2.5 text-sm min-w-0">
      <Icon className="h-4 w-4 text-muted-foreground shrink-0" strokeWidth={1.75} />
      {children ? (
        href ? (
          <a href={href} className="text-foreground/80 hover:text-primary truncate">{children}</a>
        ) : (
          <span className="text-foreground/80 truncate">{children}</span>
        )
      ) : (
        <span className="text-muted-foreground/70 italic truncate">{missing}</span>
      )}
    </p>
  );
}

function ShowroomCard({ s, onEdit }) {
  return (
    <div data-testid={`showroom-card-${s._id}`} className={cn("group bg-card border border-border rounded-2xl overflow-hidden hover:shadow-lg transition-shadow", !s.isActive && "opacity-80")}>
      <div className="relative aspect-[3/1] overflow-hidden bg-secondary">
        <img
          src={coverFor(s)}
          alt=""
          loading="lazy"
          className={cn("h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]", !s.isActive && "grayscale-[60%]")}
        />
        <span className="absolute top-3 left-3"><StatusChip active={s.isActive} /></span>
        <ShowroomMenu
          s={s}
          onEdit={onEdit}
          className="absolute top-3 right-3 h-8 w-8 rounded-full bg-card/95 shadow-sm flex items-center justify-center text-foreground hover:bg-card"
        />
      </div>
      <div className="p-5">
        <p className="font-heading text-lg leading-snug text-foreground truncate">{s.name}</p>
        <div className="mt-2 space-y-1.5">
          <ContactLine icon={MapPin} missing="No address added">{fullAddress(s)}</ContactLine>
          <ContactLine icon={Phone} href={s.phone && `tel:${s.phone.replace(/\s+/g, "")}`} missing="No phone added">{s.phone}</ContactLine>
          <ContactLine icon={Mail} href={s.email && `mailto:${s.email}`} missing="No email added">{s.email}</ContactLine>
        </div>
        <div className="mt-4 pt-4 border-t border-border flex items-center justify-between gap-3">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <CalendarDays className="h-3.5 w-3.5" /> Added {formatDate(s.createdAt)}
          </span>
          <button
            data-testid={`showroom-edit-${s._id}`}
            onClick={() => onEdit(s)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-accent/60 px-4 py-2 text-sm font-medium text-accent hover:bg-accent/10 transition-colors"
          >
            <Pencil className="h-3.5 w-3.5" /> Edit
          </button>
        </div>
      </div>
    </div>
  );
}

function ShowroomRow({ s, onEdit }) {
  return (
    <li data-testid={`showroom-row-${s._id}`} className="flex items-center gap-4 px-4 py-3 hover:bg-secondary/40 transition-colors">
      <img src={coverFor(s)} alt="" loading="lazy" className="h-14 w-20 rounded-lg object-cover shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-foreground truncate">{s.name}</p>
        <p className="text-xs text-muted-foreground truncate">{fullAddress(s) || "No address added"}</p>
      </div>
      <span className="hidden md:block w-36 text-sm text-foreground/80 truncate">{s.phone || "—"}</span>
      <span className="hidden lg:block w-56 text-sm text-foreground/80 truncate">{s.email || "—"}</span>
      <span className="hidden sm:block w-24"><StatusChip active={s.isActive} /></span>
      <ShowroomMenu s={s} onEdit={onEdit} className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary shrink-0" />
    </li>
  );
}

function FilterSelect({ icon: Icon, value, onChange, children, testId, className }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className={cn("w-full sm:w-[180px] h-11 rounded-lg bg-card", className)}>
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="h-4 w-4 text-muted-foreground shrink-0" strokeWidth={1.75} />
          <SelectValue />
        </div>
      </SelectTrigger>
      <SelectContent>{children}</SelectContent>
    </Select>
  );
}

export default function ShowroomsPage() {
  const [dialog, setDialog] = useState({ open: false, showroom: null });
  const [search, setSearch] = useState("");
  const [city, setCity] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [sort, setSort] = useState("latest");
  const [view, setView] = useState("grid");

  const { data: showrooms = [], isLoading } = useQuery({ queryKey: ["showrooms"], queryFn: () => client.get("/showrooms").then(unwrap) });

  const cities = useMemo(
    () => [...new Set(showrooms.map((s) => s.city?.trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [showrooms],
  );

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return showrooms
      .filter((s) => city === ALL || s.city?.trim() === city)
      .filter((s) => status === ALL || (status === "active") === s.isActive)
      .filter((s) => !term || [s.name, s.address, s.city, s.phone, s.email].some((v) => v?.toLowerCase().includes(term)))
      .sort(SORTS[sort].fn);
  }, [showrooms, city, status, search, sort]);

  const filtersActive = search || city !== ALL || status !== ALL;
  const clearFilters = () => {
    setSearch("");
    setCity(ALL);
    setStatus(ALL);
  };
  const openAdd = () => setDialog({ open: true, showroom: null });
  const openEdit = (showroom) => setDialog({ open: true, showroom });

  return (
    <div data-testid="showrooms-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl text-foreground">Showrooms</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Physical showroom locations for your shop. Add each location customers can visit.
          </p>
        </div>
        <Button data-testid="add-showroom-button" onClick={openAdd} className="h-11 rounded-xl px-5 bg-accent hover:bg-accent/90 text-accent-foreground shrink-0">
          <Plus className="h-4 w-4 mr-1.5" /> Add Showroom
        </Button>
      </div>

      {showrooms.length > 0 && (
        <section className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="relative flex-1 lg:max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              data-testid="showrooms-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search showrooms, address, phone…"
              className="w-full h-11 rounded-lg border border-border bg-secondary/70 pl-10 pr-4 text-sm placeholder:text-muted-foreground focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
            {cities.length > 1 && (
              <FilterSelect testId="showrooms-city-filter" icon={MapPin} value={city} onChange={setCity}>
                <SelectItem value={ALL}>All cities</SelectItem>
                {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </FilterSelect>
            )}
            <FilterSelect testId="showrooms-status-filter" icon={Tag} value={status} onChange={setStatus}>
              <SelectItem value={ALL}>All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </FilterSelect>
            <FilterSelect testId="showrooms-sort" icon={ArrowUpDown} value={sort} onChange={setSort} className="sm:w-[190px]">
              {Object.entries(SORTS).map(([key, s]) => <SelectItem key={key} value={key}>{s.label}</SelectItem>)}
            </FilterSelect>
          </div>
          {filtersActive && (
            <button data-testid="showrooms-clear-filters" onClick={clearFilters} className="text-sm font-medium text-accent hover:underline text-left lg:ml-2">
              Clear filters
            </button>
          )}
          <div className="hidden md:flex items-center gap-1 lg:ml-auto rounded-lg border border-border bg-card p-0.5" role="group" aria-label="View">
            {[["grid", LayoutGrid], ["list", List]].map(([key, Icon]) => (
              <button
                key={key}
                aria-label={`${key} view`}
                aria-pressed={view === key}
                data-testid={`showrooms-view-${key}`}
                onClick={() => setView(key)}
                className={cn("p-2 rounded-md transition-colors", view === key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary")}
              >
                <Icon className="h-4 w-4" />
              </button>
            ))}
          </div>
        </section>
      )}

      {!isLoading && showrooms.length === 0 && (
        <div data-testid="showrooms-empty" className="bg-card border border-dashed border-border rounded-2xl overflow-hidden grid md:grid-cols-2">
          <img src={showroomImageFor("empty")} alt="" className="hidden md:block h-full w-full object-cover max-h-80" />
          <div className="p-8 md:p-10 flex flex-col justify-center">
            <span className="h-12 w-12 rounded-full bg-secondary flex items-center justify-center text-primary">
              <Store className="h-5 w-5" strokeWidth={1.75} />
            </span>
            <p className="font-heading text-2xl text-foreground mt-4">Add your first showroom</p>
            <p className="text-sm text-muted-foreground mt-2 max-w-sm">
              List each location customers can visit, with its address and contact details, so your team has them in one place.
            </p>
            <Button onClick={openAdd} className="mt-5 self-start h-11 rounded-xl px-5 bg-accent hover:bg-accent/90 text-accent-foreground">
              <Plus className="h-4 w-4 mr-1.5" /> Add Showroom
            </Button>
          </div>
        </div>
      )}

      {showrooms.length > 0 && visible.length === 0 && (
        <div className="bg-card border border-dashed border-border rounded-2xl py-14 text-center">
          <p className="text-sm text-muted-foreground">No showrooms match these filters.</p>
          <button onClick={clearFilters} className="mt-2 text-sm font-medium text-accent hover:underline">Clear filters</button>
        </div>
      )}

      {visible.length > 0 &&
        (view === "grid" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
            {visible.map((s) => <ShowroomCard key={s._id} s={s} onEdit={openEdit} />)}
          </div>
        ) : (
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <ul className="divide-y divide-border">
              {visible.map((s) => <ShowroomRow key={s._id} s={s} onEdit={openEdit} />)}
            </ul>
          </div>
        ))}

      <ShowroomDialog open={dialog.open} showroom={dialog.showroom} onOpenChange={(open) => setDialog((d) => ({ ...d, open }))} />
    </div>
  );
}
